import React, {FormEvent} from 'react';
import Toggle from "components/Form/Toggle";
import {FieldArray, FieldArrayRenderProps, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import Button from "components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {AttributeFilter as AttributeFilterSpec, AttributeMatch} from "types/AudienceCriteria";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import IconName from "types/IconName";
import {Attribute, AttributeType} from "types/Attribute";
import {toTitleCase} from "services/string";
import {Option, SingleCombobox} from "../../Form/Control";

// Types ----

export type AttributeFilterProps = {
  formik: FormikHelpers<any>
  groupIndex: number
  filter?: AttributeFilterSpec | null
}

// Helpers ----

const attributeMatchTemplate: AttributeMatch = {
  attribute_id: 0,
  mode: null,
  value: '',
}

const attributeTypeIcons: Record<AttributeType, IconName> = {
  [AttributeType.BOOLEAN]: IconName.CHECK_CIRCLE,
  [AttributeType.DATE]: IconName.CALENDAR,
  [AttributeType.SELECT]: IconName.SELECTOR,
  [AttributeType.STRING]: IconName.PENCIL,
}

function findAttribute(attributes: Attribute[], id: number) {
  return attributes.find(attribute => attribute.id === id);
}

// Component ----

export default function AttributeFilter({formik, groupIndex, filter}: AttributeFilterProps) {

  const {data: attributes, isLoading: attributesLoading} = useQuery(
    ['attributes.all', {page: 1, perPage: 1000}],
    () => ngage.attributes.filtered({page: 1, perPage: 1000})
  )

  const selectableAttributes = attributes?.data.map(attribute => ({
    type: 'attribute',
    id: attribute.id,
    name: attribute.name,
    description: toTitleCase(attribute.type),
    icon: attributeTypeIcons[attribute.type]
  })) || []

  // Handle toggling this filter on and off
  function handleToggle(checked: boolean) {
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.attribute`,
      checked ? {matches: [{...attributeMatchTemplate}]} : undefined
    )
  }

  // Handle selecting an attribute
  function handleSelectAttribute(option: Option, matchIndex: number) {
    const matchAttribute = findAttribute(attributes?.data ?? [], Number(option.id));

    if (!matchAttribute) return;

    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.attribute_id`,
      matchAttribute.id
    )
  }

  // Handle changing the "has value" option
  function handleChangeHasValue(e: FormEvent<HTMLSelectElement>, match: AttributeMatch, matchIndex: number) {
    const hasValue = e.currentTarget.value === 'given';

    if (!hasValue) {
      void formik.setFieldValue(
        `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.mode`,
        null,
      );

      void formik.setFieldValue(
        `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`,
        '',
      );
    } else {
      const matchAttribute = findAttribute(attributes?.data ?? [], Number(match.attribute_id));

      void formik.setFieldValue(
        `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.mode`,
        matchAttribute?.type === 'date' ? 'between' : 'is',
      );

      if(matchAttribute?.type === 'boolean') {
        void formik.setFieldValue(
          `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`,
          'true'
        );
      }
    }
  }

  // Handling changing the date mode
  function handleChangeMode(e: FormEvent<HTMLSelectElement>, match: AttributeMatch, matchIndex: number) {
    const matchAttribute = findAttribute(attributes?.data ?? [], Number(match.attribute_id));

    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.mode`,
      e.currentTarget.value
    )

    if(matchAttribute?.type === 'date' && e.currentTarget.value !== 'between') {
      void formik.setFieldValue(
        `criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`,
        Array.isArray(match.value)? match.value.slice(0, 1) : []
      )
    }
  }

  return (
    <div className="mt-8">
      <div className="flex items-center">
        <Toggle
          label="Has attribute"
          checked={!!filter}
          onChange={handleToggle}
          loading={attributesLoading}
          disabled={attributesLoading}
        />

        {!filter && <span className="font-medium">...</span>}
      </div>

      <FieldArray name={`criteria.groups.${groupIndex}.filters.attribute.matches`}>
        {({push, remove}: FieldArrayRenderProps) => (
          <div>
            <ul className="mt-2 pl-14">
              {filter?.matches.map((match, matchIndex) => {

                const matchAttribute = findAttribute(attributes?.data ?? [], match.attribute_id);

                return (
                  <li key={`${matchIndex}-${match.attribute_id}`}>
                    <div className="flex justify-between sm:items-center">
                      <div className="sm:flex sm:items-center">
                        <div>
                          <SingleCombobox
                            isLoading={attributesLoading}
                            options={selectableAttributes}
                            selected={selectableAttributes?.find(attribute => attribute.id === match.attribute_id)}
                            onChange={option => option && handleSelectAttribute(option, matchIndex)}
                            required
                          />
                        </div>

                        {!!match.attribute_id &&
                          <>
                            <span className="ml-1 lg:mt-0 lg:ml-3 flex-shrink-0">with</span>
                            <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                              <Field
                                id={`with-${groupIndex}`}
                                as="select"
                                value={(!!match.mode) ? 'given' : 'any'}
                                onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChangeHasValue(event, match, matchIndex)}
                              >
                                <option value="any">any value</option>
                                <option value="given">given value</option>
                              </Field>
                            </div>
                          </>
                        }

                        {!!match.mode &&
                          <>
                            {/* 'Boolean' Type */}
                            {matchAttribute?.type === 'boolean' &&
                              <>
                                <span className="ml-1 lg:mt-0 lg:ml-3 flex-shrink-0">is</span>
                                <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                                  <Field
                                    as="select"
                                    name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`}
                                  >
                                    <option value="true">true</option>
                                    <option value="false">false</option>
                                  </Field>
                                </div>
                              </>
                            }

                            {/* 'Date' Type */}
                            {matchAttribute?.type === 'date' &&
                              <>
                                <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                                  <Field
                                    as="select"
                                    name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.mode`}
                                    onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChangeMode(event, match, matchIndex)}
                                  >
                                    <option value="between">between</option>
                                    <option value="on">on</option>
                                    <option value="before">before</option>
                                    <option value="after">after</option>
                                  </Field>
                                </div>

                                <div className="mt-2 sm:mt-0 sm:ml-3">
                                  <Field
                                    type="date"
                                    name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value.0`}
                                  />
                                </div>

                                <div className="sm:ml-3 sm:flex sm:items-center">
                                  {match.mode === 'between' &&
                                    <>
                                      <span className="ml-3 sm:ml-0 sm:mr-3">and</span>
                                      <Field
                                        type="date"
                                        name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value.1`}
                                      />
                                    </>
                                  }
                                </div>
                              </>
                            }

                            {/* 'Select' Type */}
                            {matchAttribute?.type === 'select' &&
                              <>
                                <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                                  <Field
                                    as="select"
                                    name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.mode`}
                                    onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChangeMode(event, match, matchIndex)}
                                  >
                                    <option value="is">is</option>
                                    <option value="not">is not</option>
                                    <option value="starts">starts with</option>
                                    <option value="doesnt-start">doesn't start with</option>
                                    <option value="ends">ends with</option>
                                    <option value="doesnt-end">doesn't end with</option>
                                    <option value="contains">contains</option>
                                    <option value="doesnt-contain">doesn't contain</option>
                                  </Field>
                                </div>

                                <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                                  {(match.mode === 'is' || match.mode === 'not') &&
                                    <Field
                                      as="select"
                                      name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`}
                                    >
                                      {matchAttribute.rules.options.map((option, index) => (
                                        <option value={option} key={`${option}-${index}`}>{option}</option>
                                      ))}
                                    </Field>
                                  }

                                  {match.mode !== 'is' && match.mode !== 'not' &&
                                    <Field
                                      type="text"
                                      name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`}
                                    />
                                  }
                                </div>
                              </>
                            }

                            {/* 'String' Type */}
                            {matchAttribute?.type === 'string' &&
                              <>
                                <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                                  <Field
                                    as="select"
                                    name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.mode`}
                                    onChange={(event: React.FormEvent<HTMLSelectElement>) => handleChangeMode(event, match, matchIndex)}
                                  >
                                    <option value="is">is</option>
                                    <option value="not">is not</option>
                                    <option value="starts">starts with</option>
                                    <option value="doesnt-start">doesn't start with</option>
                                    <option value="ends">ends with</option>
                                    <option value="doesnt-end">doesn't end with</option>
                                    <option value="contains">contains</option>
                                    <option value="doesnt-contain">doesn't contain</option>
                                  </Field>
                                </div>

                                <div className="w-32 mt-2 sm:mt-0 sm:ml-3">
                                  <Field
                                    type="text"
                                    name={`criteria.groups.${groupIndex}.filters.attribute.matches.${matchIndex}.value`}
                                  />
                                </div>
                              </>
                            }
                          </>
                        }
                      </div>

                      {(filter?.matches?.length || 0) > 1 &&
                        <Button
                          color={Button.colors.WHITE}
                          size={Button.sizes.TINY}
                          icon={TrashIcon}
                          onClick={() => remove(matchIndex)}
                        />
                      }
                    </div>

                    {matchIndex !== (filter?.matches.length || 0) - 1 &&
                      <div className="ml-3 my-4 sm:ml-0 sm:my-1 uppercase sm:normal-case">and</div>
                    }
                  </li>
                )
              })}
            </ul>

            {filter &&
              <div className="mt-4 pl-14">
                <Button
                  color={Button.colors.SECONDARY}
                  icon={PlusIcon}
                  size={Button.sizes.TINY}
                  onClick={() => push({...attributeMatchTemplate})}
                >
                  Add Attribute
                </Button>
              </div>
            }
          </div>
        )}
      </FieldArray>
    </div>
  );
}